import { Page, View, Text, Image } from '@react-pdf/renderer';
import { LaudoHeader, Amostra, Footer } from '../../../types';
import { avmPageStyles as s } from './styles';
import { AVMPageHeader, AVMSectionHeader, AVMPageFooter } from './components';

interface Page2AmostrasProps {
  header: LaudoHeader;
  amostras: Amostra[];
  footer: Footer;
  logoBase64?: string;
  metodologiaAplicada?: string;
  grauPrecisao?: string;
  grauFundamentacao?: string;
}

// w-28 = 112px, w-[452px] = 452px => total 564 ~= 563 (page 595 - 32 padding)
const PHOTO_W = 112;
const DETAILS_W = 451;

function AmostraCardAVM({ amostra, index }: { amostra: Amostra; index: number }) {
  const PLACEHOLDER_PREFIX = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAY';
  const hasValidImage = amostra.imagemUrl &&
    !amostra.imagemUrl.startsWith(PLACEHOLDER_PREFIX) &&
    (amostra.imagemUrl.startsWith('data:image/png') ||
     amostra.imagemUrl.startsWith('data:image/jpeg') ||
     amostra.imagemUrl.startsWith('data:image/jpg')) &&
    amostra.imagemUrl.length > 200;

  const tipoAbrev = amostra.tipoImovel?.toLowerCase().includes('apartamento') ? 'Apt.' :
    amostra.tipoImovel?.toLowerCase().includes('casa') ? 'Casa' :
    amostra.tipoImovel?.toLowerCase().includes('terreno') ? 'Terreno' :
    amostra.tipoImovel || '-';

  return (
    <View style={{ flexDirection: 'row', overflow: 'hidden' }} wrap={false}>
      {/* Photo: w-28 self-stretch */}
      <View style={{ width: PHOTO_W, backgroundColor: '#E7E7E7', justifyContent: 'center', alignItems: 'center', overflow: 'hidden' }}>
        {hasValidImage ? (
          <Image
            src={amostra.imagemUrl}
            style={{ width: PHOTO_W, height: PHOTO_W, objectFit: 'cover' }}
          />
        ) : (
          <Text style={{ fontSize: 7, color: '#000000', textAlign: 'center' }}>[ Foto ]</Text>
        )}
      </View>

      {/* Details: w-[452px] */}
      <View style={{ width: DETAILS_W, flexDirection: 'column', overflow: 'hidden' }}>
        {/* Dark header: AMOSTRA N + Value */}
        <View style={{ flexDirection: 'row', alignItems: 'center', backgroundColor: '#001F3F', paddingRight: 8, overflow: 'hidden' }}>
          <View style={{ width: 3, height: 28, backgroundColor: '#0090FF' }} />
          <Text style={{ flex: 1, color: '#FFFFFF', fontSize: 9, fontWeight: 'bold', paddingLeft: 8, lineHeight: 1.3 }}>
            AMOSTRA {index + 1}
          </Text>
          <Text style={{ color: '#FFFFFF', fontSize: 9, fontWeight: 'bold', lineHeight: 1.3 }}>
            {amostra.valorTotalAmostra}
          </Text>
        </View>

        {/* Row 1: Endereço (w-64=256), Tipo, Idade, Área priv */}
        <View style={{ flexDirection: 'row', paddingLeft: 8, paddingRight: 4, borderBottom: '1 solid #D6DBE7', overflow: 'hidden' }}>
          <View style={{ width: 256, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Endereço</Text>
            <Text style={s.fieldValue}>{amostra.endereco}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Tipo</Text>
            <Text style={s.fieldValue}>{tipoAbrev}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Idade</Text>
            <Text style={s.fieldValue}>{amostra.idadeAparente} anos</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Área priv.</Text>
            <Text style={s.fieldValue}>{amostra.areaPrivativa.toFixed(2)}m²</Text>
          </View>
        </View>

        {/* Row 2: Val. Unit (w-24=96), Padrão, Estado, Quartos, Banheiros, Vagas */}
        <View style={{ flexDirection: 'row', paddingLeft: 8, paddingRight: 4, borderBottom: '1 solid #D6DBE7', overflow: 'hidden' }}>
          <View style={{ width: 96, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Val. Unit. (R$/m²)</Text>
            <Text style={s.fieldValue}>{amostra.valorUnitarioR$M2}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Padrão</Text>
            <Text style={s.fieldValue}>{amostra.padraoAcabamento || '-'}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Estado</Text>
            <Text style={s.fieldValue}>{amostra.estadoConservacao || '-'}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Quartos</Text>
            <Text style={s.fieldValue}>{amostra.qtdQuartos}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Banheiros</Text>
            <Text style={s.fieldValue}>{amostra.qtdBanheiros}</Text>
          </View>
          <View style={{ flex: 1, flexDirection: 'column', gap: 2, paddingVertical: 4, overflow: 'hidden' }}>
            <Text style={s.fieldLabel}>Vagas</Text>
            <Text style={s.fieldValue}>{amostra.qtdVagas}</Text>
          </View>
        </View>

        {/* Row 3: URL */}
        {amostra.urlFonte && (
          <View style={{ paddingLeft: 8, paddingRight: 4, paddingVertical: 4, borderBottom: '1 solid #D6DBE7', overflow: 'hidden' }}>
            <Text style={s.linkText}>{amostra.urlFonte}</Text>
          </View>
        )}
      </View>
    </View>
  );
}

export function Page2AmostrasBody({
  amostras,
}: Omit<Page2AmostrasProps, 'header' | 'footer' | 'logoBase64' | 'metodologiaAplicada' | 'grauPrecisao' | 'grauFundamentacao'>) {
  if (!amostras || amostras.length === 0) return null;

  return (
    <View style={{ flexDirection: 'column', gap: 24 }}>
      <View style={{ flexDirection: 'column' }}>
        <AVMSectionHeader title="AMOSTRAS COMPARÁVEIS" />

        <View style={{ flexDirection: 'column', gap: 4 }}>
          {amostras.map((amostra, index) => (
            <AmostraCardAVM key={index} amostra={amostra} index={index} />
          ))}
        </View>
      </View>
    </View>
  );
}

export function Page2Amostras({
  header,
  amostras,
  footer,
  logoBase64,
}: Page2AmostrasProps) {
  if (!amostras || amostras.length === 0) return null;

  return (
    <Page size="A4" style={s.page}>
      <View style={s.pageContent}>
        <AVMPageHeader header={header} logoBase64={logoBase64} />
        <Page2AmostrasBody amostras={amostras} />
      </View>

      <AVMPageFooter footer={footer} />
    </Page>
  );
}
